import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { NONRKPAY_DISPUTE_REASON_STATUS } from "@constants/index";
import utils from "@utils/index";
import "./DisputeReasonCard.scss";
import { useUpdateDisputeReason } from "@queries/NonRKPayInvoiceQueries";

const DisputeReasonCard = ({
	disputeId,
	title,
	disputeReason,
	expanded,
	onClick,
	onCancel,
	onSubmit,
	children,
	isReadOnly,
}) => {
	const { lang } = global;
	const formRef = useRef(null);
	const { mutateAsync: updateDisputeReason, isLoading } = useUpdateDisputeReason();
	const [invalidFields, setInvalidFields] = useState({
		status: false,
		amendmentComment: false,
	});

	const handleSubmit = async e => {
		e.preventDefault();

		const form = formRef.current;
		const radioGroup = form.status;
		const textArea = form.amendmentComment;

		const radioValid = Array.from(radioGroup).some(radio => radio.checked);
		const textAreaValid = textArea.checkValidity();

		setInvalidFields({
			status: !radioValid,
			amendmentComment: !textAreaValid,
		});

		if (radioValid && textAreaValid) {
			try {
				await updateDisputeReason({
					disputeId,
					disputeReasonId: disputeReason.Id,
					data: {
						Status:
							radioGroup.value === "approved"
								? NONRKPAY_DISPUTE_REASON_STATUS.ACCEPTED
								: NONRKPAY_DISPUTE_REASON_STATUS.REJECTED,
						AmendmentComment: textArea.value,
					},
				});
				if (typeof onSubmit === "function") {
					onSubmit();
				}
			} catch (error) {
				utils.notify({
					type: "error",
					message: lang.t("Unable to update invoice dispute"),
				});
			}
		}
	};

	const cardClassName = classNames("dispute-reason-card");

	const amendmentCommentClassName = classNames("form-group textarea-container", {
		"invalid-field": invalidFields.amendmentComment,
	});

	const statusLabelClassName = classNames("radio margin-0", {
		"invalid-field": invalidFields.status,
	});

	const isUnanswered = disputeReason.Status === NONRKPAY_DISPUTE_REASON_STATUS.CREATED;
	const isAccepted = disputeReason.Status === NONRKPAY_DISPUTE_REASON_STATUS.ACCEPTED;
	const isRejected = disputeReason.Status === NONRKPAY_DISPUTE_REASON_STATUS.REJECTED;

	/**
	 * This status comes from the enum that exists in the backend
	 * however, it only has two values: CREATED and AMENDED, if
	 * we want to handle the REJECTED status, the backend would need
	 * to be updated first.
	 */
	const statusClassName = classNames("label", {
		"label-default": isUnanswered,
		"label-success": isAccepted,
		"label-danger": isRejected,
	});
	const statusText = {
		[NONRKPAY_DISPUTE_REASON_STATUS.CREATED]: lang.t("Unanswered"),
		[NONRKPAY_DISPUTE_REASON_STATUS.ACCEPTED]: lang.t("Accepted"),
		[NONRKPAY_DISPUTE_REASON_STATUS.REJECTED]: lang.t("Rejected"),
	}[disputeReason.Status];

	return (
		<div
			aria-expanded={expanded}
			className={cardClassName}
			role={!expanded && !isReadOnly ? "button" : undefined}
			onClick={!expanded && !isReadOnly ? onClick : undefined}
			onKeyDown={
				!expanded && !isReadOnly
					? e => (e.key === "Enter" || e.key === " ") && onClick()
					: undefined
			}
		>
			{statusText && <span className={statusClassName}>{statusText}</span>}
			<h4>{title}</h4>
			{children && (
				<div className="margin-top-16 flex flex-direction-column flex-gap-8">
					{children}
					{disputeReason.AmendmentComment && !isUnanswered && (
						<p className="margin-bottom-0">
							{`${lang.t("Amendment Comment")}: ${disputeReason.AmendmentComment}`}
						</p>
					)}
				</div>
			)}
			{!expanded && !isReadOnly && (
				<button
					type="button"
					title={lang.t("Click to answer")}
					className="btn btn-link padding-left-0 padding-right-0 margin-top-16"
					onClick={e => {
						e.stopPropagation();
						onClick();
					}}
				>
					{lang.t("Click to answer")}
				</button>
			)}
			{expanded && (
				<form
					noValidate
					ref={formRef}
					onSubmit={handleSubmit}
					className="margin-bottom-0 margin-top-16"
				>
					<div className="form-group">
						<div className="flex flex-direction-row flex-justify-content-start flex-align-items-center flex-gap-8">
							<div className={statusLabelClassName}>
								<label htmlFor="status_approve">
									<input
										required
										type="radio"
										name="status"
										value="approved"
										id="status_approve"
										defaultChecked={
											disputeReason.Status ===
											NONRKPAY_DISPUTE_REASON_STATUS.ACCEPTED
										}
									/>
									{lang.t("Accept")}
								</label>
							</div>
							<div className={statusLabelClassName}>
								<label htmlFor="status_deny">
									<input
										type="radio"
										name="status"
										value="denied"
										id="status_deny"
										defaultChecked={
											disputeReason.Status ===
											NONRKPAY_DISPUTE_REASON_STATUS.REJECTED
										}
									/>
									{lang.t("Reject")}
								</label>
							</div>
						</div>
					</div>
					<div className={amendmentCommentClassName}>
						<label htmlFor="amendmentComment">
							{lang.t("Amendment Comment")}
							<span className="required-mark" />
						</label>
						<textarea
							required
							name="amendmentComment"
							id="amendmentComment"
							className="form-control"
							rows={4}
							defaultValue={disputeReason.AmendmentComment || ""}
						/>
					</div>
					<div className="flex flex-direction-row flex-gap-8 margin-top-16">
						<button
							disabled={isLoading}
							className="btn btn-primary"
							type="submit"
						>
							{!isLoading && lang.t("Save")}
							{isLoading && (
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							)}
						</button>
						<button
							disabled={isLoading}
							className="btn btn-link"
							type="button"
							onClick={onCancel}
						>
							{lang.t("Cancel")}
						</button>
					</div>
				</form>
			)}
		</div>
	);
};

DisputeReasonCard.defaultProps = {
	expanded: false,
	children: null,
	isReadOnly: false,
};

DisputeReasonCard.propTypes = {
	title: PropTypes.string.isRequired,
	disputeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	disputeReason: PropTypes.shape({
		Status: PropTypes.string.isRequired,
		Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		AmendmentComment: PropTypes.string,
	}).isRequired,
	expanded: PropTypes.bool,
	onClick: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	children: PropTypes.node,
	onSubmit: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

export default DisputeReasonCard;
